import { nanoid } from 'nanoid';

import type { ChunkAckMessage, ChunkMessage } from '../shared/websocket/ChunkMessages';

type SendChunkMessage = (message: ChunkMessage) => void;

export const MessageLimitToBeChunked = 64 * 1024;

export class ChunkSender {
  public readonly id = nanoid();

  private readonly send: SendChunkMessage;

  private chunks: string[] = [];

  private chunkAckIntervals: Record<number, NodeJS.Timeout> = {};

  constructor({ message, sendChunk }: { message: any; sendChunk: SendChunkMessage }) {
    this.send = sendChunk;

    this.calculateChunks(message);
  }

  static needToBeChunked(message: any): boolean {
    return JSON.stringify(message).length > MessageLimitToBeChunked;
  }

  static isChunkAckMessage(message: any): message is ChunkAckMessage {
    if (typeof message !== 'object') {
      return false;
    }

    return 'type' in message && message.type === 'chunk-ack';
  }

  sendChunks() {
    this.chunks.forEach((chunk, index) => {
      const message: ChunkMessage = {
        type: 'chunk',
        id: this.id,
        chunk,
        totalChunks: this.chunks.length,
        chunkNumber: index,
      };

      this.send(message);
      this.chunkAckIntervals[index] = setInterval(() => {
        this.send(message);
      }, 10000);
    });
  }

  private calculateChunks(message: any) {
    const messageAsString =
      typeof message !== 'string' ? JSON.stringify(message) : message;

    const chunks = [];

    for (let i = 0; i < messageAsString.length; i += MessageLimitToBeChunked) {
      chunks.push(messageAsString.slice(i, i + MessageLimitToBeChunked));
    }

    this.chunks = chunks;
  }

  onChunkAck(message: ChunkAckMessage) {
    clearInterval(this.chunkAckIntervals[message.chunkNumber]);

    delete this.chunkAckIntervals[message.chunkNumber];
  }

  get isCompleted() {
    return Object.keys(this.chunkAckIntervals).length === 0;
  }

  dispose() {
    Object.values(this.chunkAckIntervals).forEach(clearInterval);
  }
}
